import { endOfDay, startOfDay, sub } from "date-fns";
import {
  ActionType,
  AuthStateChangedAction,
} from "src/contexts/auth/firebase-context";
import { getNsDataFromCollection } from "src/pages/dashboard/helpers/nsapihelpers";
import { getGlucoseData } from "src/pages/dashboard/helpers/helpers";
import {
  emptyProfilesStoreInformation,
  GlucObject,
  Profile,
  ProfilesStoreInformation,
} from "src/pages/dashboard/helpers/typehelpers";
import { Deployments, Status } from "src/types/deployments";
import { Distribution3Days, User } from "src/types/user";
import { deepCopy } from "src/utils/deep-copy";
import { t } from "i18next";

export const handleUnauthorizedResponse = () => {
  localStorage.removeItem("access_token");
  window.location.href = "/auth/firebase/login";
};

export const updateProfile = async (
  user: User | null,
  data: Record<
    string,
    | string
    | number
    | object
    | boolean
    | Array<string | number | object | boolean>
  >,
  dispatch: (value: AuthStateChangedAction) => void
) => {
  if (!user) {
    console.error("User not found");
    return;
  }
  const accessToken = localStorage.getItem("access_token");
  try {
    const response = await fetch(
      process.env.REACT_APP_BE_URL + "/users/profile-update",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ ...data, id: user.id }),
      }
    );

    if (response.status === 401) {
      handleUnauthorizedResponse();
      return;
    }

    if (response.ok) {
      const data = await response.json();
      data &&
        dispatch({
          type: ActionType.AUTH_STATE_CHANGED,
          payload: {
            isAuthenticated: true,
            user: {
              ...user,
              ...data,
            },
          },
        });
    } else {
      console.error("Failed to update profile");
    }
  } catch (error) {
    console.error("Error updating profile:", error);
  }
};

export const updateEnvVars = async (
  user: User | null,
  data: Record<string, string | number>,
  deployment: string
): Promise<{ error: { error: boolean; message: string } }> => {
  if (!user?.nsInstance?.validated) {
    console.error(t("UserNSInstanceNotFound"));
    return { error: { error: true, message: t("UserNSInstanceNotFound") } };
  }
  try {
    const accessToken = localStorage.getItem("access_token");
    const response = await fetch(
      process.env.REACT_APP_BE_URL +
        `/update-env?namespace=${user.nsInstance.id}&deployment=${deployment}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ ...data, id: user.id }),
      }
    );

    if (response.status === 401) {
      handleUnauthorizedResponse();
      return { error: { error: true, message: "Unauthorized" } };
    }

    if (!response.ok) {
      return { error: { error: true, message: response.statusText } };
    }

    return { error: { error: false, message: "" } };
  } catch (error) {
    console.error("Failed to update env vars");
    return { error: { error: true, message: error.toString() } };
  }
};

export const importData = async (
  user: User | null,
  connectionString: string
) => {
  if (!user?.nsInstance?.validated) {
    console.error(t("UserNSInstanceNotFound"));
    throw new Error(t("UserNSInstanceNotFound"));
  }
  try {
    const accessToken = localStorage.getItem("access_token");
    const response = await fetch(
      process.env.REACT_APP_BE_URL +
        `/import-mongodb-uri?namespace=${user.nsInstance.id}&uri=${connectionString}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (response.status === 401) {
      handleUnauthorizedResponse();
      return;
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to import data");
    return error;
  }
};

export const importStatus = async (taskId: string) => {
  try {
    const accessToken = localStorage.getItem("access_token");
    const response = await fetch(
      process.env.REACT_APP_BE_URL + `/import-status?task_id=${taskId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (response.status === 401) {
      handleUnauthorizedResponse();
      return;
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to check import status");
    return error;
  }
};

export const deleteAccount = async (
  user: User | null,
  dispatch: (value: AuthStateChangedAction) => void
) => {
  const emailUrl = `mailto:info@opensource.clinic?subject=Delete%20my%20account&body=I%20want%20to%20delete%20my%20account.%20My%20email%20is%20${user?.email}`;
  window.open(emailUrl, "_blank");
  return;
};

export const getUsers = async (): Promise<User[]> => {
  try {
    const accessToken = localStorage.getItem("access_token");
    const response = await fetch(process.env.REACT_APP_BE_URL + "/users/list", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (response.status === 401) {
      handleUnauthorizedResponse();
      return [];
    }

    if (response.ok) {
      const users = await response.json();
      return deepCopy(users);
    }
    console.error("Failed to fetch users");
    return [];
  } catch (error) {
    console.error("Error fetching users:", error);
    return [];
  }
};

export const getUser = async (id: string): Promise<Profile | undefined> => {
  let user: Profile;
  try {
    const response = await fetch(
      process.env.REACT_APP_BE_URL + "/users/search?key=" + id + "&refer=id"
    );
    // https://typeone-be-1f614fbf9608.herokuapp.com/users/search/?key=Kelvin%20Kramp&refer=name

    if (response.ok) {
      user = await response.json();
      return deepCopy(user)[0];
    }
    console.error("Failed to fetch users");
    return undefined;
  } catch {
    console.error("Failed to fetch users");
    return undefined;
  }
};

export const emptyDistribution = {
  inRange: undefined,
  low: undefined,
  high: undefined,
};

export const getDistribution3Days = async (
  user: User
): Promise<Distribution3Days> => {
  if (!user?.nsInstance?.validated) {
    console.error(t("UserNSInstanceNotFound"));
    return emptyDistribution;
  }
  const glucoseSeries: GlucObject[] = await getGlucoseData(user, {
    start: startOfDay(sub(new Date(), { days: 1 })),
    end: endOfDay(new Date()),
  });
  return getDistrubtion(user, glucoseSeries, "percentage");
};

export const getDistrubtion = async (
  user: User | null,
  glucSeries: GlucObject[],
  outputType: "percentage" | "number"
) => {
  if (!user) return emptyDistribution;
  const { envVars } = await getEnvVariables(user, Deployments.NIGHTSCOUT);
  if (!envVars) return emptyDistribution;
  const targetHigh = envVars.BG_HIGH;
  const targetLow = envVars.BG_LOW;
  const percentageOutput = outputType === "percentage";
  const high =
    glucSeries.filter((gluc) => gluc.value > Number(targetHigh)).length /
    (percentageOutput ? (1 / 100) * glucSeries.length : 1);
  const low =
    glucSeries.filter((gluc) => gluc.value <= Number(targetLow)).length /
    (percentageOutput ? (1 / 100) * glucSeries.length : 1);

  let inRange: number;
  if (percentageOutput) {
    inRange = 100 - high - low;
  } else {
    inRange = glucSeries.filter(
      (gluc) =>
        gluc.value > Number(targetLow) && gluc.value <= Number(targetHigh)
    ).length;
  }

  if (outputType === "percentage") {
    return {
      inRange: Number(inRange.toFixed(1)),
      low: Number(low.toFixed(1)),
      high: Number(high.toFixed(1)),
    };
  } else {
    return { inRange: inRange, low: low, high: high };
  }
};

export const getProfile = async (
  user: User | null
): Promise<ProfilesStoreInformation[]> => {
  if (!user?.nsInstance?.validated) return [emptyProfilesStoreInformation];
  const endpoint = `profile`;
  try {
    const { data } = await getNsDataFromCollection(
      user,
      endpoint,
      undefined,
      "v1"
    );
    return data as ProfilesStoreInformation[];
  } catch (error) {
    console.error("Error fetching:", error);
    return [emptyProfilesStoreInformation];
  }
};

export interface Subscription {
  name: string;
  email: string;
  place?: string;
  phone?: string;
  workshop?: string;
  phoneType?: "android" | "ios";
  role?: string;
  cgm?: string;
  message?: string;
  route?: string;
  privacyPolicy?: boolean;
  newsLetter?: boolean;
  date: string;
}
export const subscribeNewsLetter = async (input: Subscription) => {
  const response = await fetch(
    process.env.REACT_APP_BE_URL + "/newsletter/subscribe",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    }
  );
  return response;
};

/** Newsletter API expects `{ name, email }` (see `/newsletter/subscribe`). */
export type NewsletterSubscribeInput = { name: string; email: string };

export type NewsletterSubscribeResult =
  | { ok: true; message: string }
  | { ok: false; error: string };

export const subscribeNewsletterMinimal = async (
  input: NewsletterSubscribeInput
): Promise<NewsletterSubscribeResult> => {
  const response = await fetch(
    process.env.REACT_APP_BE_URL + "/newsletter/subscribe",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: input.name.trim(),
        email: input.email.trim(),
      }),
    }
  );

  let data: { error?: string; message?: string } = {};
  try {
    data = await response.json();
  } catch {
    // non-JSON body
  }

  if (!response.ok) {
    return {
      ok: false,
      error:
        data.error ||
        `Subscription failed${
          response.statusText ? ` (${response.statusText})` : ""
        }`,
    };
  }

  return {
    ok: true,
    message: data.message || "Thank you for subscribing!",
  };
};

export const subscribeWorkshop = async (input: Subscription) => {
  const response = await fetch(
    process.env.REACT_APP_BE_URL + "/workshop/subscribe",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    }
  );
  return response;
};

export const getEnvVariables = async (
  user: User | null,
  deployment: string
) => {
  if (!deployment) {
    console.error(t("NoDeploymentNameProvided"));
    return {
      status: Status.ERROR,
      envVars: {},
      error: t("NoDeploymentNameProvided"),
    };
  }
  if (!user?.nsInstance?.validated) {
    console.error(t("UserNSInstanceNotFound"));
    return {
      status: Status.ERROR,
      envVars: {},
      error: t("UserNSInstanceNotFound"),
    };
  }

  if (!user?.roles?.includes("nightscout")) {
    console.error(t("UserIsNotANightscoutUser"));
    return {
      status: Status.ERROR,
      envVars: {},
      error: t("UserIsNotANightscoutUser"),
    };
  }

  try {
    const accessToken = localStorage.getItem("access_token");
    const response = await fetch(
      `${process.env.REACT_APP_BE_URL}/get-env?namespace=${user.nsInstance.id}&deployment=${deployment}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (response.status === 401) {
      console.log("Unauthorized: ", response.status);
      handleUnauthorizedResponse();
      return { status: Status.ERROR, envVars: {}, error: "Unauthorized" };
    }

    if (!response.ok) {
      return {
        status: Status.ERROR,
        envVars: {},
        error: "Response not OK:" + response.statusText,
      };
    }
    // Pro memori: if deployment doesnt exist a 206 http respons is returned")
    const data: {
      status: Status;
      env_vars: Record<string, string>;
      error?: string;
    } = await response.json();
    return {
      status: data.status,
      envVars: data.env_vars, // will be undefined when no deployment
      error: data.error,
    };
  } catch (error) {
    console.error("Error fetching environment variables:", error);
    return {
      status: Status.ERROR,
      envVars: {},
      error: "Error fetching environment variables",
    };
  }
};

export const getIpAddress = async (namespaceId: string | undefined) => {
  if (!namespaceId) {
    console.error(t("NoNamespaceIdFound"));
    return;
  }
  try {
    const accessToken = localStorage.getItem("access_token");
    const response = await fetch(
      `${process.env.REACT_APP_BE_URL}/get-ip?namespace=${namespaceId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (response.status === 401) {
      handleUnauthorizedResponse();
      return;
    }

    const data = await response.json();
    let ipAddress = data["external_ip"];
    const withHttp = ipAddress.includes("http");
    if (!withHttp) {
      ipAddress = `http://${ipAddress}`;
    }
    return ipAddress;
  } catch (error) {
    console.error("Error fetching IP address:", error);
    throw error;
  }
};

export const getFullUrl = (cname: string | undefined) => {
  if (!cname) {
    console.log("No cname found");
    return;
  }
  const base = process.env.REACT_APP_DOMAIN_NAME?.toLowerCase();
  const url = `https://${cname}.${base}`;
  return url;
};

export const getPods = async (user: User | null) => {
  try {
    const accessToken = localStorage.getItem("access_token");
    const response = await fetch(
      `${process.env.REACT_APP_BE_URL}/get-namespace-content?namespace=${user?.nsInstance?.id}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(t("FailedToFetchDeploymentStatus"));
    }

    const data = await response.json();
    const pods = data["pods"];
    const statuses = pods.map(
      (pod: { status: string; name: string }) => pod.status
    );
    const status = statuses.every((status: string) => status === "Running")
      ? "Running"
      : statuses.some((status: string) => status === "Pending")
      ? "Pending"
      : "Stopped";
    return { status: status, pods: pods };
  } catch (error) {
    console.error("Error fetching deployment status:", error);
    throw error;
  }
};

export const exportMongoDB = async (user: User | null) => {
  if (!user?.nsInstance?.validated) {
    console.error(t("UserNSInstanceNotFound"));
    throw new Error(t("UserNSInstanceNotFound"));
  }

  try {
    const accessToken = localStorage.getItem("access_token");
    const response = await fetch(
      `${process.env.REACT_APP_BE_URL}/export-mongodb-pod?namespace=${user.nsInstance.id}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (response.status === 401) {
      handleUnauthorizedResponse();
      return;
    }

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    // Get the filename from the Content-Disposition header if available
    const contentDisposition = response.headers.get("Content-Disposition");
    const now = new Date();
    const timestamp = now.toISOString().slice(0, -5); // Format: YYYY-MM-DDTHH-mm-ss
    let filename = `mongodb_export_${timestamp}.tar`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    // Convert the response to a blob
    const blob = await response.blob();

    // Create a download link and trigger the download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    // Cleanup
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    return { success: true };
  } catch (error) {
    console.error("Failed to export MongoDB data:", error);
    return { success: false };
  }
};

export const getIncomeData = async () => {
  const accessToken = localStorage.getItem("access_token");
  const response = await fetch(
    `${process.env.REACT_APP_BE_URL}/get-stripe-income`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  const data = await response.json();
  return data;
};

export const getTransipInvoices = async () => {
  try {
    const accessToken = localStorage.getItem("access_token");
    const response = await fetch(
      `${process.env.REACT_APP_BE_URL}/get-transip-invoices`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (response.ok) {
      const data = await response.json();
      return data;
    }

    console.error("Failed to fetch TransIP invoices");
    return [];
  } catch (error) {
    console.error("Error fetching TransIP invoices:", error);
    return [];
  }
};
